import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  forwardRef,
  inject,
  input,
  signal,
  computed,
  effect,
  ElementRef,
  viewChild,
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR, ReactiveFormsModule, FormGroup, FormControl } from '@angular/forms';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import type { IconCategoryGroup, IconResponse } from '@zag/core/models';
import { DEFAULT_COLOR_PALETTE, type ColorOption } from '../color-picker/color-picker';
import { ZagIcon } from '../icon/icon';
import { Input as ZagInput } from '../input/input';
import { ColorContrastService } from '../services/color-contrast.service';
import { IconsService } from '../services/icons.service';

/**
 * Appearance mode - determines whether to show icon/color or image
 */
export type AppearanceMode = 'icon' | 'image';

/**
 * Appearance value object
 */
export interface AppearanceValue {
  icon: string | null;
  color: string | null;
  imageUrl: string | null;
}

/**
 * Default appearance value
 */
export const DEFAULT_APPEARANCE: AppearanceValue = {
  icon: null,
  color: null,
  imageUrl: null,
};

/**
 * Maximum file size for image uploads (2MB)
 */
const MAX_FILE_SIZE = 2 * 1024 * 1024;

/**
 * Allowed image MIME types
 */
const ALLOWED_IMAGE_TYPES = new Set(['image/jpeg', 'image/png', 'image/gif', 'image/webp', 'image/svg+xml']);

const HEX_COLOR_PATTERN = /^#[0-9A-Fa-f]{6}$/;

/**
 * Appearance editor component combining IconPicker, ColorPicker, and image upload.
 * Supports two modes: icon/color mode and image mode (URL or file upload).
 * Emits a single AppearanceValue object containing all three fields.
 *
 * @example Basic usage with mode toggle
 * ```html
 * <zag-appearance-editor
 *   [(ngModel)]="entityAppearance"
 *   [showModeToggle]="true"
 * />
 * ```
 *
 * @example Icon/color only (no image support)
 * ```html
 * <zag-appearance-editor
 *   [(ngModel)]="entityAppearance"
 *   [showModeToggle]="false"
 *   [showImageUrl]="false"
 * />
 * ```
 */
@Component({
  selector: 'zag-appearance-editor',
  templateUrl: './appearance-editor.html',
  styleUrl: './appearance-editor.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [ReactiveFormsModule, TranslatePipe, ZagIcon, ZagInput],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => AppearanceEditor),
      multi: true,
    },
  ],
  host: {
    class: 'zag-appearance-editor',
    '[class.zag-appearance-editor--disabled]': 'disabled()',
  },
})
export class AppearanceEditor implements ControlValueAccessor {
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly colorContrast = inject(ColorContrastService);
  protected readonly iconsService = inject(IconsService);
  private readonly translate = inject(TranslateService);

  /** Whether to show the mode toggle (icon/color vs image) */
  readonly showModeToggle = input<boolean>(true);

  /** Whether to show the image URL/upload section (when mode toggle is hidden) */
  readonly showImageUrl = input<boolean>(true);

  /** Whether to show the URL input field in image mode (set to false for upload-only) */
  readonly showImageUrlInput = input<boolean>(true);

  /** Whether the editor is disabled */
  readonly disabled = input<boolean>(false);

  /** Custom color palette */
  readonly colors = input<ColorOption[] | undefined>(undefined);

  /** Labels */
  readonly iconLabel = input<string>('Icon');
  readonly colorLabel = input<string>('Color');
  readonly imageUrlLabel = input<string>('Image URL');
  readonly uploadLabel = input<string>('Upload Image');

  /** Placeholders */
  readonly iconPlaceholder = input<string>('Select an icon');
  readonly colorPlaceholder = input<string>('Select a color');
  readonly imageUrlPlaceholder = input<string>('https://example.com/image.png');

  /** Default icon shown when no explicit icon is assigned */
  readonly defaultIcon = input<string | null>(null);

  /** Mode toggle labels */
  readonly iconModeLabel = input<string>('Icon & Color');
  readonly imageModeLabel = input<string>('Image');

  /** Default/initial mode (useful when mode toggle is hidden) */
  readonly defaultMode = input<AppearanceMode>('icon');

  /** Reference to the hidden file input */
  private readonly fileInput = viewChild<ElementRef<HTMLInputElement>>('fileInput');

  /** Current appearance mode */
  readonly mode = signal<AppearanceMode>('icon');

  /** Error message for file upload */
  readonly uploadError = signal<string | null>(null);

  /** Whether a file is being processed */
  readonly isProcessingFile = signal<boolean>(false);

  /** Internal form group */
  protected readonly form = new FormGroup({
    icon: new FormControl<string | null>(null),
    color: new FormControl<string | null>(null),
    imageUrl: new FormControl<string | null>(null),
  });

  /** Internal value signal */
  private readonly internalValue = signal<AppearanceValue>(DEFAULT_APPEARANCE);

  /** Inline search and custom color state */
  protected readonly iconSearchQuery = signal<string>('');
  protected readonly customColorInput = signal<string>('');

  /** Computed preview: show icon with color */
  readonly previewIcon = computed(() => this.internalValue().icon ?? this.defaultIcon());
  readonly previewColor = computed(() => this.internalValue().color);
  readonly previewImageUrl = computed(() => this.internalValue().imageUrl);

  /** Compute icon color based on background brightness for contrast */
  readonly previewIconColor = computed<string | null>(() => {
    if (this.previewColor()) {
      return this.colorContrast.getIconColor(this.previewColor());
    }

    return this.previewIcon() ? this.colorContrast.getIconColor(null) : null;
  });

  readonly hasFallbackPreviewBackground = computed(() => {
    return !this.previewColor() && !!this.previewIcon();
  });

  /** Available inline color palette */
  readonly availableColors = computed(() => this.colors() ?? DEFAULT_COLOR_PALETTE);

  /** Icon groups filtered by search query */
  readonly filteredIconGroups = computed(() => {
    const query = this.iconSearchQuery().toLowerCase().trim();
    const groups = this.iconsService.groupedIcons();

    if (!query) {
      return groups;
    }

    return groups
      .map((group: IconCategoryGroup) => ({
        ...group,
        icons: group.icons.filter(
          (icon: IconResponse) =>
            icon.name.toLowerCase().includes(query) ||
            icon.label.toLowerCase().includes(query) ||
            icon.tags.some((tag: string) => tag.toLowerCase().includes(query))
        ),
      }))
      .filter((group: IconCategoryGroup) => group.icons.length > 0);
  });

  /** Whether the custom color input is valid */
  readonly isCustomColorValid = computed(() => {
    return HEX_COLOR_PATTERN.test(this.customColorInput());
  });

  /** Whether there's any appearance set */
  readonly hasAppearance = computed(() => {
    const value = this.internalValue();
    return !!(value.icon || value.color || value.imageUrl);
  });

  /** Whether there's an icon/color appearance (for preview logic) */
  readonly hasIconAppearance = computed(() => {
    const value = this.internalValue();
    return !!(value.icon || value.color || this.defaultIcon());
  });

  /** Whether there's an image appearance */
  readonly hasImageAppearance = computed(() => {
    return !!this.internalValue().imageUrl;
  });

  /**
   * Whether to show the preview section at all.
   * When mode toggle is enabled, show preview based on current mode's content.
   * When mode toggle is disabled (legacy), show preview if any appearance is set.
   */
  readonly shouldShowPreview = computed(() => {
    if (this.showModeToggle()) {
      // In toggle mode, show preview based on current mode
      if (this.mode() === 'icon') {
        return this.hasIconAppearance();
      } else {
        return this.hasImageAppearance();
      }
    }
    // Legacy mode: show if any appearance is set
    return this.hasAppearance();
  });

  /**
   * Whether to show the image in preview.
   * When mode toggle is enabled, only show image if in image mode.
   * When mode toggle is disabled (legacy), show image if it exists (takes priority).
   */
  readonly shouldShowImagePreview = computed(() => {
    if (this.showModeToggle()) {
      return this.mode() === 'image' && this.hasImageAppearance();
    }
    // Legacy mode: image takes priority
    return this.hasImageAppearance();
  });

  /**
   * Whether to show the icon/color in preview.
   * When mode toggle is enabled, only show icon if in icon mode.
   * When mode toggle is disabled (legacy), show icon if no image is set.
   */
  readonly shouldShowIconPreview = computed(() => {
    if (this.showModeToggle()) {
      return this.mode() === 'icon' && this.hasIconAppearance();
    }
    // Legacy mode: show icon only if no image
    return !this.hasImageAppearance() && this.hasIconAppearance();
  });

  /**
   * Determines which type of preview to show.
   * Returns 'image', 'icon', or 'none'.
   */
  readonly previewType = computed<'image' | 'icon' | 'none'>(() => {
    if (this.shouldShowImagePreview()) return 'image';
    if (this.shouldShowIconPreview()) return 'icon';
    return 'none';
  });

  // ControlValueAccessor callbacks
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  private onChange: (value: AppearanceValue) => void = () => {};
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  private onTouched: () => void = () => {};

  /** Track if mode has been initialized */
  private modeInitialized = false;

  constructor() {
    // Initialize mode from defaultMode input (runs once on first read)
    effect(() => {
      const defaultMode = this.defaultMode();
      if (!this.modeInitialized) {
        this.mode.set(defaultMode);
        this.modeInitialized = true;
      }
    });

    // Sync form changes to internal value and emit
    effect(() => {
      const disabled = this.disabled();
      if (disabled) {
        this.form.disable({ emitEvent: false });
      } else {
        this.form.enable({ emitEvent: false });
      }
    });

    // Watch previewType changes and trigger change detection
    // This ensures the @switch block properly updates when mode changes
    effect(() => {
      // Read the signal to track it
      this.previewType();
      // Schedule change detection for next microtask
      queueMicrotask(() => this.cdr.detectChanges());
    });

    // Subscribe to form value changes
    this.form.valueChanges.subscribe(formValue => {
      const value: AppearanceValue = {
        icon: formValue.icon ?? null,
        color: formValue.color ?? null,
        imageUrl: formValue.imageUrl ?? null,
      };
      this.internalValue.set(value);
      this.onChange(value);
    });
  }

  writeValue(value: AppearanceValue | null): void {
    const normalizedValue = value ?? DEFAULT_APPEARANCE;
    this.internalValue.set(normalizedValue);
    this.form.patchValue(normalizedValue, { emitEvent: false });
    this.customColorInput.set(this.getInitialCustomColor(normalizedValue.color));

    // Auto-detect mode based on value
    if (normalizedValue.imageUrl) {
      this.mode.set('image');
    } else if (normalizedValue.icon || normalizedValue.color) {
      this.mode.set('icon');
    }
  }

  registerOnChange(fn: (value: AppearanceValue) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    const action = isDisabled ? 'disable' : 'enable';
    this.form[action]({ emitEvent: false });
  }

  /** Switch appearance mode */
  setMode(newMode: AppearanceMode): void {
    if (this.disabled()) return;
    this.mode.set(newMode);
    this.uploadError.set(null);
    // Schedule change detection for next microtask to ensure the @if block
    // has time to process signal changes and create/destroy embedded views
    queueMicrotask(() => this.cdr.detectChanges());
  }

  /** Clear all appearance fields */
  clearAll(): void {
    this.form.patchValue({
      icon: null,
      color: null,
      imageUrl: null,
    });
    this.customColorInput.set('');
    this.uploadError.set(null);
  }

  /** Clear only icon/color fields */
  clearIconColor(): void {
    this.form.patchValue({
      icon: null,
      color: null,
    });
    this.customColorInput.set('');
  }

  /** Clear only image field */
  clearImage(): void {
    this.form.patchValue({
      imageUrl: null,
    });
    this.uploadError.set(null);
  }

  /** Trigger file input click */
  triggerFileUpload(): void {
    if (this.disabled()) return;
    this.fileInput()?.nativeElement.click();
  }

  /** Handle file selection */
  onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];

    if (!file) {
      return;
    }

    // Reset input so the same file can be selected again
    input.value = '';

    // Validate file type
    if (!ALLOWED_IMAGE_TYPES.has(file.type)) {
      this.uploadError.set(this.translate.instant('common.appearanceEditor.errors.invalidFileType'));
      return;
    }

    // Validate file size
    if (file.size > MAX_FILE_SIZE) {
      this.uploadError.set(this.translate.instant('common.appearanceEditor.errors.fileTooLarge'));
      return;
    }

    this.uploadError.set(null);
    this.isProcessingFile.set(true);

    // Convert to base64
    const reader = new FileReader();
    reader.onload = () => {
      const base64 = reader.result as string;
      this.form.patchValue({ imageUrl: base64 });
      this.isProcessingFile.set(false);
    };
    reader.onerror = () => {
      this.uploadError.set(this.translate.instant('common.appearanceEditor.errors.fileReadFailed'));
      this.isProcessingFile.set(false);
    };
    reader.readAsDataURL(file);
  }

  selectIcon(icon: IconResponse): void {
    this.form.patchValue({ icon: icon.name });
    this.onTouched();
  }

  selectColor(color: ColorOption): void {
    this.form.patchValue({ color: color.value });
    this.customColorInput.set('');
    this.onTouched();
  }

  applyCustomColor(): void {
    if (!this.isCustomColorValid() || this.disabled()) {
      return;
    }

    this.form.patchValue({ color: this.customColorInput() });
    this.onTouched();
  }

  onIconSearchInput(event: Event): void {
    const input = event.target as HTMLInputElement;
    this.iconSearchQuery.set(input.value);
  }

  onCustomColorInput(event: Event): void {
    const input = event.target as HTMLInputElement;
    let value = input.value.trim();

    if (value && !value.startsWith('#')) {
      value = `#${value}`;
    }

    this.customColorInput.set(value);
  }

  isIconSelected(icon: IconResponse): boolean {
    return this.internalValue().icon === icon.name;
  }

  isColorSelected(color: ColorOption): boolean {
    return this.internalValue().color === color.value;
  }

  /** Called when any field is blurred */
  onFieldBlur(): void {
    this.onTouched();
  }

  private getInitialCustomColor(color: string | null): string {
    if (!color || this.availableColors().some(option => option.value === color)) {
      return '';
    }

    return color;
  }
}
